import {
  useMemo,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '@/widgets/layout/ui/AppLayout'
import { Button, Select, Skeleton, SkeletonStack } from '@/shared/ui'
import type { Target, TargetResult, TargetState } from '@/shared/api/types'
import {
  CAMPAIGN_STATUS,
  TARGET_RESULT,
  TARGET_STATE,
} from '@/entities/contact'
import { useCampaign, useCampaignControl, useCampaignTargets } from '@/entities/campaign'
import { useOutcomes } from '@/entities/call'
import { useBots } from '@/entities/bot'
import css from './CampaignPage.module.css'

const TARGETS_PAGE_SIZE = 25

type StateFilter = TargetState | ''
type ResultFilter = TargetResult | ''

const STATE_OPTIONS = [
  { value: '', label: 'Все состояния' },
  { value: 'queued', label: TARGET_STATE.queued },
  { value: 'dialing', label: TARGET_STATE.dialing },
  { value: 'done', label: TARGET_STATE.done },
  { value: 'skipped', label: TARGET_STATE.skipped },
]

const RESULT_OPTIONS = [
  { value: '', label: 'Любой результат' },
  { value: 'talked', label: TARGET_RESULT.talked },
  { value: 'no_answer', label: TARGET_RESULT.no_answer },
  { value: 'busy', label: TARGET_RESULT.busy },
  { value: 'failed', label: TARGET_RESULT.failed },
  { value: 'blocked', label: TARGET_RESULT.blocked },
  { value: 'cancelled', label: TARGET_RESULT.cancelled },
]

export function CampaignPage() {
  const bots = useBots()
  const botNames = bots.data?.names ?? {}
  const { id } = useParams<{ id: string }>()
  const parsedId = Number(id)
  const campaignId = Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null
  const campaign = useCampaign(campaignId)
  const control = useCampaignControl(campaignId ?? 0)
  const outcomes = useOutcomes()
  const navigate = useNavigate()

  const [state, setState] = useState<StateFilter>('')
  const [result, setResult] = useState<ResultFilter>('')
  const [page, setPage] = useState(0)
  const [controlError, setControlError] = useState('')

  const targets = useCampaignTargets(
    campaignId,
    {
      state: state || undefined,
      result: result || undefined,
      limit: TARGETS_PAGE_SIZE,
      offset: page * TARGETS_PAGE_SIZE,
    },
    campaign.data?.status === 'running',
  )

  const outcomeLabels = useMemo(
    () => new Map((outcomes.data ?? []).map((option) => [option.value, option.label])),
    [outcomes.data],
  )

  if (campaignId === null) {
    return (
      <>
        <PageHeader title="Запуск не найден" description="В адресе указан некорректный номер запуска." />
        <RemoteState
          title="Проверьте ссылку"
          message="Вернитесь к списку запусков и откройте нужный обзвон оттуда."
          action={
            <Button variant="secondary" onClick={() => navigate('/campaigns')}>
              Ко всем запускам
            </Button>
          }
        />
      </>
    )
  }

  if (campaign.isPending) {
    return <CampaignLoading />
  }

  if (campaign.isError) {
    return (
      <>
        <PageHeader title="Обзвон" />
        <RemoteState
          error
          title="Не удалось загрузить запуск"
          message={errorMessage(campaign.error)}
          action={
            <Button variant="secondary" onClick={() => void campaign.refetch()}>
              Повторить
            </Button>
          }
        />
      </>
    )
  }

  if (!campaign.data) {
    return (
      <>
        <PageHeader title="Запуск не найден" />
        <RemoteState
          title="Такого запуска нет"
          message="Возможно, он был удалён или ссылка устарела."
          action={
            <Button variant="secondary" onClick={() => navigate('/campaigns')}>
              Ко всем запускам
            </Button>
          }
        />
      </>
    )
  }

  const info = campaign.data
  const status = CAMPAIGN_STATUS[info.status]
  const botLabel = info.botId ? (botNames[String(info.botId)] ?? 'бот удалён') : 'основной'
  const processed = Math.min(info.progress.total, info.progress.done + info.progress.skipped)
  const progressPercent = info.progress.total
    ? Math.min(100, Math.round((processed / info.progress.total) * 100))
    : 0
  const pageCount = Math.max(1, Math.ceil((targets.data?.total ?? 0) / TARGETS_PAGE_SIZE))
  const outcomeEntries = Object.entries(info.progress.outcomes)
    .filter(([, count]) => count > 0)
    .sort((left, right) => right[1] - left[1])

  async function runControl(action: 'pause' | 'resume' | 'stop') {
    const confirmations = {
      pause: 'Поставить этот обзвон на паузу?',
      resume: 'Продолжить этот обзвон?',
      stop: 'Остановить этот обзвон? Вернуть его в очередь после остановки не получится.',
    }
    if (!window.confirm(confirmations[action])) return

    setControlError('')
    try {
      await control.mutateAsync(action)
    } catch (error) {
      setControlError(errorMessage(error))
    }
  }

  function setStateFilter(next: StateFilter) {
    setState(next)
    setPage(0)
  }

  function setResultFilter(next: ResultFilter) {
    setResult(next)
    setPage(0)
  }

  return (
    <>
      <div className={css.breadcrumbs}>
        <Link to="/campaigns">Обзвоны</Link>
        <span aria-hidden="true">/</span>
        <span>{info.name}</span>
      </div>

      <div className={css.headerRow}>
        <PageHeader
          title={info.name}
          description={`Создан ${formatDateTime(info.createdAt)} · бот: ${botLabel} · одновременно ${info.concurrency} · ожидание ответа ${info.ringTimeout} с`}
        >
          <div className={css.headerMeta}>
            <span className={css.badge} data-tone={status.tone}>
              {status.label}
            </span>
            {info.status === 'running' && (
              <span className={css.liveMeta}>
                <span className={css.liveDot} aria-hidden="true" />
                Данные обновляются автоматически
              </span>
            )}
          </div>
        </PageHeader>

        <div className={css.controls} aria-label="Управление обзвоном">
          {info.status === 'running' && (
            <Button
              variant="secondary"
              loading={control.isPending && control.variables === 'pause'}
              onClick={() => void runControl('pause')}
            >
              Пауза
            </Button>
          )}
          {info.status === 'paused' && (
            <Button
              variant="primary"
              loading={control.isPending && control.variables === 'resume'}
              onClick={() => void runControl('resume')}
            >
              Продолжить
            </Button>
          )}
          {(info.status === 'running' || info.status === 'paused') && (
            <Button
              variant="danger"
              loading={control.isPending && control.variables === 'stop'}
              onClick={() => void runControl('stop')}
            >
              Остановить
            </Button>
          )}
        </div>
      </div>

      {controlError && (
        <div className={css.inlineError} role="alert">
          {controlError}
        </div>
      )}

      {info.status === 'paused' && info.pauseReason && (
        <div className={css.inlineError} role="status">
          Запуск остановился сам: {info.pauseReason}.
        </div>
      )}

      {info.waitingHours && (
        <div className={css.waitingBanner} role="status">
          <span className={css.waitingIcon} aria-hidden="true">
            <ClockIcon />
          </span>
          <div>
            <strong>Сейчас вне рабочих часов</strong>
            <p>
              Новые звонки не начинаются. Очередь продолжится автоматически, когда откроется
              расписание компании.
            </p>
          </div>
        </div>
      )}

      <div className={css.overviewGrid}>
        <section className={css.progressPanel} aria-labelledby="progress-title">
          <div className={css.sectionHeader}>
            <div>
              <h2 id="progress-title">Прогресс</h2>
              <p>
                Пройдено {processed.toLocaleString('ru-RU')} из{' '}
                {info.progress.total.toLocaleString('ru-RU')}
              </p>
            </div>
            <strong className={css.progressPercent}>{progressPercent}%</strong>
          </div>

          <div
            className={css.progressTrack}
            role="progressbar"
            aria-label="Прогресс обзвона"
            aria-valuemin={0}
            aria-valuemax={info.progress.total || 1}
            aria-valuenow={processed}
            aria-valuetext={`${processed} из ${info.progress.total}`}
          >
            <span
              className={`${css.progressFill} ${info.status === 'running' ? css.progressRunning : ''}`}
              style={{ width: `${progressPercent}%` }}
            >
              {info.status === 'running' && progressPercent > 0 && progressPercent < 100 && (
                <span className={css.progressPulse} aria-hidden="true" />
              )}
            </span>
          </div>

          <div className={css.progressLegend} aria-label="Состояния очереди">
            <ProgressLegend label="В очереди" value={info.progress.queued} tone="neutral" />
            <ProgressLegend label="Звоним" value={info.progress.dialing} tone="live" />
            <ProgressLegend label="Завершено" value={info.progress.done} tone="success" />
            <ProgressLegend label="Пропущено" value={info.progress.skipped} tone="warning" />
          </div>
        </section>

        <section className={css.currentPanel} aria-labelledby="current-title">
          <div className={css.sectionHeader}>
            <div>
              <h2 id="current-title">Сейчас звонит</h2>
              <p>Активные линии этого запуска</p>
            </div>
            {info.current.length > 0 && <span className={css.currentCount}>{info.current.length}</span>}
          </div>

          {info.current.length > 0 ? (
            <div className={css.currentList} aria-live="polite">
              {info.current.map((target) => (
                <div key={target.id} className={css.currentCall}>
                  <span className={css.callPulse} aria-hidden="true" />
                  <div className={css.currentPerson}>
                    {target.contactId ? (
                      <Link to={`/clients/${target.contactId}`}>
                        {target.displayName || target.phoneFormatted}
                      </Link>
                    ) : (
                      <strong>{target.displayName || target.phoneFormatted}</strong>
                    )}
                    {target.displayName && <span>{target.phoneFormatted}</span>}
                  </div>
                  <span className={css.currentAttempt}>попытка {target.attempts}</span>
                  {target.callId && (
                    <Link className={css.iconLink} to={`/calls/${target.callId}`} aria-label="Открыть звонок">
                      →
                    </Link>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className={css.currentEmpty}>
              <span className={css.quietDot} aria-hidden="true" />
              <span>{currentEmptyText(info.status, info.waitingHours, info.progress.queued)}</span>
            </div>
          )}
        </section>
      </div>

      <section className={css.kpiGrid} aria-label="Показатели запуска" aria-live="polite">
        <KpiTile
          label="Пройдено"
          value={`${processed.toLocaleString('ru-RU')} / ${info.progress.total.toLocaleString('ru-RU')}`}
        />
        <KpiTile label="Поговорили" value={info.progress.talked.toLocaleString('ru-RU')} tone="success" />
        <KpiTile label="Не ответили" value={info.progress.noAnswer.toLocaleString('ru-RU')} tone="warning" />
        <KpiTile label="Ошибки" value={info.progress.failed.toLocaleString('ru-RU')} tone="danger" />
      </section>

      {outcomeEntries.length > 0 && (
        <section className={css.outcomesPanel} aria-labelledby="outcomes-title">
          <div className={css.sectionHeader}>
            <div>
              <h2 id="outcomes-title">Итоги разговоров</h2>
              <p>Разбор завершённых звонков</p>
            </div>
          </div>
          <div className={css.outcomeList}>
            {outcomeEntries.map(([outcome, count]) => (
              <div key={outcome} className={css.outcomeItem}>
                <span>{outcomeLabels.get(outcome) ?? readableOutcome(outcome)}</span>
                <strong>{count.toLocaleString('ru-RU')}</strong>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className={css.targetsPanel} aria-labelledby="targets-title">
        <div className={css.targetsHeader}>
          <div>
            <h2 id="targets-title">Номера запуска</h2>
            <p>Очередь, результаты и связанные карточки клиентов.</p>
          </div>
          <div className={css.targetFilters}>
            <Select
              aria-label="Фильтр по состоянию номера"
              value={state}
              options={STATE_OPTIONS}
              onChange={(event) => setStateFilter(event.target.value as StateFilter)}
            />
            <Select
              aria-label="Фильтр по результату звонка"
              value={result}
              options={RESULT_OPTIONS}
              onChange={(event) => setResultFilter(event.target.value as ResultFilter)}
            />
          </div>
        </div>

        {targets.isPending ? (
          <div className={css.loadingBlock} aria-label="Загрузка номеров запуска">
            <SkeletonStack rows={7} height={42} />
          </div>
        ) : targets.isError ? (
          <RemoteState
            compact
            error
            title="Не удалось загрузить номера"
            message={errorMessage(targets.error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void targets.refetch()}>
                Повторить
              </Button>
            }
          />
        ) : (targets.data?.items.length ?? 0) === 0 ? (
          <RemoteState
            compact
            title={state || result ? 'По фильтрам ничего не найдено' : 'В запуске нет номеров'}
            message={
              state || result
                ? 'Выберите другое состояние или результат.'
                : 'Список целей появится после создания запуска.'
            }
          />
        ) : (
          <>
            <div className={css.tableScroll}>
              <table className={css.targetsTable}>
                <thead>
                  <tr>
                    <th scope="col">Клиент</th>
                    <th scope="col">Состояние</th>
                    <th scope="col">Результат</th>
                    <th scope="col">Попытки</th>
                    <th scope="col">Следующий звонок</th>
                    <th scope="col">Итог / ошибка</th>
                    <th scope="col">Ссылки</th>
                  </tr>
                </thead>
                <tbody>
                  {targets.data?.items.map((target) => (
                    <TargetRow
                      key={target.id}
                      target={target}
                      outcomeLabel={
                        outcomeLabels.get(target.outcome) ?? readableOutcome(target.outcome)
                      }
                      onOpen={(path) => navigate(path)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            <TargetsPagination
              page={page}
              pageCount={pageCount}
              total={targets.data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </section>
    </>
  )
}

function CampaignLoading() {
  return (
    <>
      <PageHeader title="Обзвон" />
      <div className={css.loadingHero} aria-label="Загрузка запуска">
        <Skeleton height={110} radius="var(--radius-lg)" />
        <div className={css.loadingKpis}>
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} height={88} radius="var(--radius-lg)" />
          ))}
        </div>
        <SkeletonStack rows={6} height={46} />
      </div>
    </>
  )
}

function ProgressLegend({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'neutral' | 'live' | 'success' | 'warning'
}) {
  return (
    <div className={css.legendItem} data-tone={tone}>
      <span className={css.legendDot} aria-hidden="true" />
      <span>{label}</span>
      <strong>{value.toLocaleString('ru-RU')}</strong>
    </div>
  )
}

function KpiTile({
  label,
  value,
  tone = 'neutral',
}: {
  label: string
  value: string
  tone?: 'neutral' | 'success' | 'warning' | 'danger'
}) {
  return (
    <div className={css.kpiTile} data-tone={tone}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function TargetRow({
  target,
  outcomeLabel,
  onOpen,
}: {
  target: Target
  outcomeLabel: string
  onOpen: (path: string) => void
}) {
  const destination = target.callId
    ? `/calls/${target.callId}`
    : target.contactId
      ? `/clients/${target.contactId}`
      : null
  const stateTone = targetStateTone(target.state)
  const resultTone = targetResultTone(target.result)

  function openFromClick(event: ReactMouseEvent<HTMLTableRowElement>) {
    if (!destination || isInteractiveTarget(event.target)) return
    onOpen(destination)
  }

  function openFromKeyboard(event: ReactKeyboardEvent<HTMLTableRowElement>) {
    if (!destination || event.target !== event.currentTarget || event.key !== 'Enter') return
    event.preventDefault()
    onOpen(destination)
  }

  return (
    <tr
      className={css.targetRow}
      tabIndex={destination ? 0 : undefined}
      role={destination ? 'link' : undefined}
      aria-label={destination ? `Открыть ${target.displayName || target.phoneFormatted}` : undefined}
      onClick={openFromClick}
      onKeyDown={openFromKeyboard}
    >
      <td data-label="Клиент">
        {target.contactId ? (
          <Link
            className={css.personLink}
            to={`/clients/${target.contactId}`}
            onClick={(event) => event.stopPropagation()}
          >
            <span>{target.displayName || target.phoneFormatted}</span>
            {target.displayName && <small>{target.phoneFormatted}</small>}
          </Link>
        ) : (
          <span className={css.personLink}>
            <span>{target.displayName || target.phoneFormatted}</span>
            {target.displayName && <small>{target.phoneFormatted}</small>}
          </span>
        )}
      </td>
      <td data-label="Состояние">
        <span className={css.badge} data-tone={stateTone}>
          {target.state === 'dialing' && <span className={css.badgePulse} aria-hidden="true" />}
          {TARGET_STATE[target.state]}
        </span>
      </td>
      <td data-label="Результат">
        <span className={css.badge} data-tone={resultTone}>
          {TARGET_RESULT[target.result]}
        </span>
      </td>
      <td className={css.numberCell} data-label="Попытки">
        {target.attempts}
      </td>
      <td className={css.dateCell} data-label="Следующий звонок">
        {target.nextAt ? formatDateTime(target.nextAt) : '—'}
      </td>
      <td className={css.outcomeCell} data-label="Итог / ошибка">
        {target.error ? (
          <span className={css.errorText} title={target.error}>
            {target.error}
          </span>
        ) : target.outcome ? (
          <span title={outcomeLabel}>{outcomeLabel}</span>
        ) : (
          <span className={css.muted}>—</span>
        )}
      </td>
      <td data-label="Ссылки">
        <span className={css.rowLinks}>
          {target.contactId && (
            <Link to={`/clients/${target.contactId}`} onClick={(event) => event.stopPropagation()}>
              Клиент
            </Link>
          )}
          {target.callId && (
            <Link to={`/calls/${target.callId}`} onClick={(event) => event.stopPropagation()}>
              Звонок
            </Link>
          )}
          {!target.contactId && !target.callId && <span className={css.muted}>—</span>}
        </span>
      </td>
    </tr>
  )
}

function TargetsPagination({
  page,
  pageCount,
  total,
  onPage,
}: {
  page: number
  pageCount: number
  total: number
  onPage: (page: number) => void
}) {
  if (total === 0) return null

  return (
    <nav className={css.pagination} aria-label="Страницы номеров запуска">
      <span>
        {total.toLocaleString('ru-RU')} {plural(total, 'номер', 'номера', 'номеров')}
      </span>
      <div>
        <Button
          variant="secondary"
          size="sm"
          disabled={page === 0}
          onClick={() => onPage(Math.max(0, page - 1))}
        >
          Назад
        </Button>
        <span className={css.pageNumber} aria-live="polite">
          {page + 1} из {pageCount}
        </span>
        <Button
          variant="secondary"
          size="sm"
          disabled={page + 1 >= pageCount}
          onClick={() => onPage(Math.min(pageCount - 1, page + 1))}
        >
          Далее
        </Button>
      </div>
    </nav>
  )
}

function RemoteState({
  title,
  message,
  action,
  error = false,
  compact = false,
}: {
  title: string
  message: string
  action?: React.ReactNode
  error?: boolean
  compact?: boolean
}) {
  return (
    <section
      className={`${css.remoteState} ${compact ? css.remoteStateCompact : ''}`}
      role={error ? 'alert' : undefined}
    >
      <span className={error ? css.errorIcon : css.emptyIcon} aria-hidden="true">
        {error ? '!' : <QueueIcon />}
      </span>
      <strong>{title}</strong>
      <p>{message}</p>
      {action}
    </section>
  )
}

function targetStateTone(state: TargetState): string {
  if (state === 'dialing') return 'success'
  if (state === 'skipped') return 'warning'
  return 'neutral'
}

function targetResultTone(result: TargetResult): string {
  if (result === 'talked') return 'success'
  if (result === 'no_answer' || result === 'busy') return 'warning'
  if (result === 'failed' || result === 'blocked' || result === 'cancelled') return 'danger'
  return 'neutral'
}

function currentEmptyText(status: string, waitingHours: boolean, queued: number): string {
  if (waitingHours) return 'Очередь ждёт начала рабочих часов.'
  if (status === 'paused') return 'Запуск на паузе — активных звонков нет.'
  if (status === 'stopped') return 'Запуск остановлен.'
  if (status === 'done') return 'Все звонки завершены.'
  if (queued > 0) return 'Готовится следующий номер из очереди.'
  return 'Активных звонков сейчас нет.'
}

function readableOutcome(value: string): string {
  if (!value) return '—'
  return value.replaceAll('_', ' ')
}

function formatDateTime(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function plural(value: number, one: string, few: string, many: string): string {
  const mod10 = value % 10
  const mod100 = value % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few
  return many
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  return 'Что-то пошло не так. Попробуйте ещё раз.'
}

function isInteractiveTarget(target: EventTarget): boolean {
  return target instanceof Element && Boolean(target.closest('a, button, input, select, textarea, label'))
}

function ClockIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M10 5.8V10.2L13 12" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function QueueIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" aria-hidden="true">
      <path d="M6 8H22M6 14H18M6 20H15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="3.5" cy="8" r="1" fill="currentColor" />
      <circle cx="3.5" cy="14" r="1" fill="currentColor" />
      <circle cx="3.5" cy="20" r="1" fill="currentColor" />
    </svg>
  )
}
